import { Extension } from "@tiptap/core";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";

import { nodeIsExpression } from "@/components/tiptap/tiptap-expression/utils";

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    highlightInsertedNodes: {
      /**
       * Insert content and automatically highlight the inserted nodes
       * @param content - The content to insert
       * @param pos - Optional position where to insert the content (like insertContentAt)
       */
      insertContentAndHighlight: (
        content: Parameters<Commands<ReturnType>["insertContent"]["insertContent"]>[0],
        pos?: number | { from: number; to: number },
      ) => ReturnType;
    };
  }
}

// Plugin key for managing highlight state
const highlightPluginKey = new PluginKey<HighlightState>("highlightInsertedNodes");

interface HighlightState {
  decorations: DecorationSet;
}

interface DecorationSpec {
  insertionId: string;
}

interface AddHighlightMeta {
  action: "add";
  positions: number[];
  insertionId: string;
}

interface RemoveHighlightMeta {
  action: "remove";
  insertionId: string;
}

type HighlightMeta = AddHighlightMeta | RemoveHighlightMeta;

/**
 * Extension to support highlighting newly inserted nodes
 * Uses decorations to add ephemeral visual highlights that don't get persisted
 */
export const HighlightInsertedNodes = Extension.create({
  name: "highlightInsertedNodes",

  addProseMirrorPlugins() {
    return [
      new Plugin<HighlightState>({
        key: highlightPluginKey,

        state: {
          init() {
            return { decorations: DecorationSet.empty };
          },

          apply(tr, pluginState, _oldState, newState) {
            // Get highlight instructions from transaction metadata
            const highlightMeta = tr.getMeta(highlightPluginKey) as HighlightMeta | undefined;

            if (highlightMeta?.action === "add") {
              // Add decorations to the specified positions
              const { positions, insertionId } = highlightMeta;
              const newDecorations: Decoration[] = [];

              for (const nodePos of positions) {
                try {
                  const node = newState.doc.nodeAt(nodePos);
                  if (node && (node.isBlock || node.type.name === "image")) {
                    // Add a node decoration with the data attribute and insertionId in spec
                    newDecorations.push(
                      Decoration.node(
                        nodePos,
                        nodePos + node.nodeSize,
                        {
                          "data-newly-inserted": insertionId,
                        },
                        { insertionId }, // Store insertionId in spec for later filtering
                      ),
                    );
                  }
                } catch (error) {
                  console.warn("Failed to add highlight decoration:", error);
                }
              }

              // Add new decorations to existing ones
              return {
                decorations: pluginState.decorations.add(newState.doc, newDecorations),
              };
            }

            if (highlightMeta?.action === "remove") {
              // Remove only decorations matching this insertionId
              const { insertionId } = highlightMeta;
              const decorations = pluginState.decorations.find(
                undefined,
                undefined,
                (spec) => (spec as DecorationSpec).insertionId !== insertionId,
              );

              return {
                decorations: DecorationSet.create(newState.doc, decorations),
              };
            }

            // Map decorations through document changes
            return {
              decorations: pluginState.decorations.map(tr.mapping, tr.doc),
            };
          },
        },

        props: {
          decorations(state) {
            return highlightPluginKey.getState(state)?.decorations;
          },
        },
      }),
    ];
  },

  addCommands() {
    return {
      /**
       * Insert content and automatically highlight the inserted nodes
       */
      insertContentAndHighlight:
        (
          content: Parameters<typeof this.editor.commands.insertContent>[0],
          pos?: number | { from: number; to: number },
        ) =>
        ({ commands, state, editor }) => {
          // Get the current position and document size before insertion
          let posBeforeInsert = typeof pos === "number" ? pos : (pos?.from ?? state.selection.from);
          const docSizeBeforeInsert = state.doc.content.size;

          const $pos = state.doc.resolve(posBeforeInsert);
          let adjustedPos = pos;

          // Check if we're inserting at an expression node
          // If so, insert after the end of the current line to avoid replacing expression content
          if (nodeIsExpression(state.doc.nodeAt(posBeforeInsert))) {
            // Find the end of the paragraph/line containing the expression
            const endOfLinePos = $pos.end($pos.depth);
            posBeforeInsert = endOfLinePos;
            adjustedPos = endOfLinePos;
          }

          // Check if we're inserting into an empty block node
          const $posForEmptyCheck = state.doc.resolve(posBeforeInsert);
          const nodeAtInsertPos = $posForEmptyCheck.parent;
          const isEmptyBlock =
            nodeAtInsertPos.isBlock &&
            nodeAtInsertPos.content.size === 0 &&
            $posForEmptyCheck.depth > 0;
          const emptyBlockPos = isEmptyBlock
            ? $posForEmptyCheck.before($posForEmptyCheck.depth)
            : null;

          const inserted = adjustedPos
            ? commands.insertContentAt(adjustedPos, content)
            : commands.insertContent(content);

          if (!inserted) {
            return false;
          }

          // Schedule the highlighting for the next frame
          requestAnimationFrame(() => {
            const currentState = editor.state;
            const docSizeAfterInsert = currentState.doc.content.size;
            const insertedSize = docSizeAfterInsert - docSizeBeforeInsert;

            const insertionId = Date.now().toString();
            const { doc } = currentState;
            const insertEndPos = posBeforeInsert + insertedSize;

            // Collect positions of block nodes to highlight
            const nodesToHighlight: number[] = [];

            // Find all top-level block nodes in the inserted range
            // We only want to highlight the outermost blocks, not nested ones
            doc.nodesBetween(posBeforeInsert, insertEndPos, (node, nodePos) => {
              // Only mark block nodes (and image nodes) that start at or after the insertion point.
              // Skip empty blocks (e.g., empty paragraphs used for spacing), but always include images.
              const isImageNode = node.type.name === "image";
              const isCodeBlockNode = node.type.name === "codeBlock";

              if (
                nodePos >= posBeforeInsert &&
                nodePos < insertEndPos &&
                ((node.isBlock && node.content.size > 0) || isImageNode || isCodeBlockNode)
              ) {
                nodesToHighlight.push(nodePos);
                // Return false to prevent descending into children
                // This ensures we only highlight top-level nodes (e.g., list, not its items)
                return false;
              }
            });

            // If we were inserting into an empty block and haven't found any new nodes,
            // highlight that existing block (content was added to it)
            if (
              emptyBlockPos !== null &&
              nodesToHighlight.length === 0 &&
              emptyBlockPos < doc.content.size
            ) {
              const nodeAtPos = doc.nodeAt(emptyBlockPos);
              if (nodeAtPos && nodeAtPos.isBlock && nodeAtPos.content.size > 0) {
                nodesToHighlight.push(emptyBlockPos);
              }
            }

            if (nodesToHighlight.length > 0) {
              // Add decorations via transaction metadata
              const tr = currentState.tr.setMeta(highlightPluginKey, {
                action: "add",
                positions: nodesToHighlight,
                insertionId,
              });
              editor.view.dispatch(tr);

              // Remove decorations after animation completes (1s to match CSS)
              setTimeout(() => {
                const removeTr = editor.state.tr.setMeta(highlightPluginKey, {
                  action: "remove",
                  insertionId,
                });
                editor.view.dispatch(removeTr);
              }, 1000); // Match CSS animation duration
            }
          });

          return true;
        },
    };
  },
});
